import { offSiteDistanceSchema } from "@/modules/private-lessons/domain/lib/off-site-distance";
import { FilterConditionSchema } from "@/shared/contracts/base";
import { parseTimeToDate } from "@/shared/lib/date-time";
import { z } from "zod";
import {
  normalizeAndValidateSessionInstructors,
  SessionInstructorsValidationError,
} from "../lib/session-instructors";

export const sessionLocationKindSchema = z.enum(["studio", "off_site"]);
export const sessionEngagementKindSchema = z.enum([
  "standard",
  "partner_instructor_only",
]);

export const sessionInstructorRoleSchema = z.enum(["lead", "assist"]);
export const SessionInstructorAssignmentSchema = z.object({
  instructorId: z.string().min(1),
  role: sessionInstructorRoleSchema,
});
export type SessionInstructorAssignmentDTO = z.infer<
  typeof SessionInstructorAssignmentSchema
>;

export const ClassSessionInstructorDTOSchema = z.object({
  instructorId: z.string(),
  name: z.string(),
  role: sessionInstructorRoleSchema,
});


function refineSessionInstructors(
  data: {
    instructors?: SessionInstructorAssignmentDTO[];
    instructorIds?: string[];
    engagementKind?: "standard" | "partner_instructor_only";
  },
  ctx: z.RefinementCtx,
) {
  try {
    normalizeAndValidateSessionInstructors({
      instructors: data.instructors,
      instructorIds: data.instructorIds,
      engagementKind: data.engagementKind ?? "standard",
    });
  } catch (err) {
    const message =
      err instanceof SessionInstructorsValidationError
        ? err.message
        : "Invalid instructors";
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message,
      path: data.instructors != null ? ["instructors"] : ["instructorIds"],
    });
  }
}

/**
 * Class Session Schema - Session คลาสเรียน
 */
export const ClassSessionSchema = z.object({
  id: z.string(),
  templateId: z.string(),
  templateName: z.string(), // Class Type + Level
  classTypeId: z.string(),
  classTypeName: z.string(),
  levelId: z.string(),
  levelName: z.string(),
  date: z.string(), // YYYY-MM-DD
  startTime: z.string(), // HH:mm:ss
  endTime: z.string(), // HH:mm:ss
  locationKind: sessionLocationKindSchema,
  offSiteLabel: z.string().nullable(),
  offSiteDistance: offSiteDistanceSchema.nullable(),
  engagementKind: sessionEngagementKindSchema,
  roomId: z.string().nullable(),
  roomName: z.string(),
  instructors: z.array(ClassSessionInstructorDTOSchema),
  instructorIds: z.array(z.string()),
  instructorNames: z.array(z.string()),
  capacity: z.number(),
  status: z.enum(["scheduled", "completed", "cancelled"]),
  notes: z.string().nullable(),
  attendanceCount: z.number().optional(), // จำนวนคนที่เช็กชื่อแล้ว
  /** จำนวนครูที่ check-in แล้ว (calendar / hub) */
  instructorCheckInCount: z.number().optional(),
  createdBy: z.string().nullable(),
  createdByUserName: z.string().optional(), // Join from users table
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ClassSessionDTO = z.infer<typeof ClassSessionSchema>;

/**
 * Create Class Session Schema
 */
export const CreateClassSessionSchema = z
  .object({
    templateId: z.string().min(1, "ຕ້ອງເລືອກ template"),
    date: z.string(),
    startTime: z.string(),
    endTime: z.string(),
    /** client เก่าไม่ส่งได้ — default studio; ส่ง null จะถือเป็นไม่ระบุ */
    locationKind: sessionLocationKindSchema
      .nullish()
      .transform((v) => v ?? "studio"),
    offSiteLabel: z.union([z.string(), z.null()]).optional(),
    offSiteDistance: offSiteDistanceSchema.nullish(),
    engagementKind: sessionEngagementKindSchema
      .nullish()
      .transform((v) => v ?? "standard"),
    roomId: z.string().min(1).optional(),
    /** Preferred: lead + TA roles */
    instructors: z.array(SessionInstructorAssignmentSchema).optional(),
    /** Legacy: first = lead, rest = assist (max 3) */
    instructorIds: z.array(z.string()).optional(),
    capacity: z
      .number()
      .int("ຄວາມຈຸຕ້ອງເປັນຈຳນວນເຕັມ")
      .positive("ຄວາມຈຸຕ້ອງເປັນຈຳນວນບວກ"),
    notes: z.string().nullable().optional(),
  })
  .refine(
    (data) =>
      data.locationKind !== "studio" ||
      (data.roomId != null && data.roomId.length > 0),
    {
      message: "ຕ້ອງເລືອກຫ້ອງ",
      path: ["roomId"],
    },
  )
  .refine(
    (data) =>
      data.locationKind !== "off_site" ||
      data.roomId == null ||
      data.roomId.length === 0,
    {
      message: "ສຳລັບຄລາດສ໌ນອກສະຖານທີ່ບໍ່ຕ້ອງກຳນົດຫ້ອງສະຕູດິໂອ",
      path: ["roomId"],
    },
  )
  .refine(
    (data) =>
      data.locationKind !== "studio" || data.offSiteDistance == null,
    {
      message: "ໄລຍະນອກສະຖານທີ່ຕ້ອງວ່າງເມື່ອເປັນສະຕູດິໂອ",
      path: ["offSiteDistance"],
    },
  )
  .refine(
    (data) => {
      const start = parseTimeToDate(data.startTime);
      const end = parseTimeToDate(data.endTime);
      return end > start;
    },
    {
      message: "ເວລາສິ້ນສຸດຕ້ອງຫຼາຍກວ່າເວລາເລີ່ມຕົ້ນ",
      path: ["endTime"],
    },
  )
  .superRefine((data, ctx) => refineSessionInstructors(data, ctx));
export type CreateClassSessionDTO = z.infer<typeof CreateClassSessionSchema>;

/**
 * Bulk Create Class Sessions — Recurrence Pattern
 * แต่ละวันในสัปดาห์ผูกกับเวลาและห้องของตัวเอง
 * dayOfWeek: 1=Mon, 2=Tue, ..., 7=Sun
 */
/** ກຳນົດປະເພດທູກວັນ · ແຊຣ໌ກັບ ຂາຍ partner / ສ້າງກຸ່ມ */
export const BulkClassSessionDayScheduleSchema = z.object({
  dayOfWeek: z.number().int().min(1).max(7),
  startTime: z.string().min(1, "ຕ້ອງເລືອກເວລາເລີ່ມຕົ້ນ"),
  endTime: z.string().min(1, "ຕ້ອງເລືອກເວລາສິ້ນສຸດ"),
  /** ມີເມື່ອ locationKind = studio; ບໍ່ສົ່ງຫຼືວ່າງເມື່ອ off_site */
  roomId: z.string().min(1).optional(),
});

export const BulkCreateClassSessionsSchema = z
  .object({
    templateId: z.string().min(1, "ຕ້ອງເລືອກແມ່ແບບຄລາດສ໌"),
    dateFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "ວັນທີບໍ່ຖືກຕ້ອງ"),
    dateTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "ວັນທີບໍ່ຖືກຕ້ອງ"),
    locationKind: sessionLocationKindSchema
      .nullish()
      .transform((v) => v ?? "studio"),
    offSiteLabel: z.union([z.string(), z.null()]).optional(),
    offSiteDistance: offSiteDistanceSchema.nullish(),
    engagementKind: sessionEngagementKindSchema
      .nullish()
      .transform((v) => v ?? "standard"),
    daySchedules: z
      .array(BulkClassSessionDayScheduleSchema)
      .min(1, "ຕ້ອງເລືອກວັນຢ່າງໜ້ອຍ 1 ວັນ ແລະ ກຳນົດເວລາ (ຫ້ອງເມື່ອເປັນສະຕູດິໂອ)"),
    /** Preferred: lead + TA roles */
    instructors: z.array(SessionInstructorAssignmentSchema).optional(),
    /** Legacy: first = lead, rest = assist (max 3) */
    instructorIds: z.array(z.string()).optional(),
    capacity: z
      .number()
      .int("ຄວາມຈຸຕ້ອງເປັນຈຳນວນເຕັມ")
      .positive("ຄວາມຈຸຕ້ອງເປັນຈຳນວນບວກ"),
    notes: z.string().nullable().optional(),
  })
  .refine(
    (d) =>
      d.locationKind !== "studio" ||
      d.daySchedules.every(
        (s) => s.roomId != null && String(s.roomId).trim().length > 0,
      ),
    {
      message: "ເມື່ອເປັນສະຕູດິໂອຕ້ອງມີຫ້ອງທຸກລາຍການວັນ",
      path: ["daySchedules"],
    },
  )
  .refine(
    (d) =>
      d.locationKind !== "off_site" ||
      d.daySchedules.every(
        (s) => s.roomId == null || String(s.roomId).trim().length === 0,
      ),
    {
      message: "ຄລາດສ໌ນອກສະຖານທີ່ບໍ່ຕ້ອງກຳນົດຫ້ອງສະຕູດິໂອ",
      path: ["daySchedules"],
    },
  )
  .refine(
    (d) => d.locationKind !== "studio" || d.offSiteDistance == null,
    {
      message: "ໄລຍະນອກສະຖານທີ່ຕ້ອງວ່າງເມື່ອເປັນສະຕູດິໂອ",
      path: ["offSiteDistance"],
    },
  )
  .refine((d) => d.dateFrom <= d.dateTo, {
    message: "ວັນທີສິ້ນສຸດຕ້ອງບໍ່ໃຫ້ເກີນວັນທີເລີ່ມຕົ້ນ",
    path: ["dateTo"],
  })
  .refine(
    (d) => {
      for (const s of d.daySchedules) {
        const start = parseTimeToDate(s.startTime);
        const end = parseTimeToDate(s.endTime);
        if (end <= start) return false;
      }
      return true;
    },
    {
      message: "ເວລາສິ້ນສຸດຕ້ອງຫຼາຍກວ່າເວລາເລີ່ມຕົ້ນ",
      path: ["daySchedules"],
    },
  )
  .superRefine((data, ctx) => refineSessionInstructors(data, ctx));

export type BulkClassSessionDayScheduleDTO = z.infer<
  typeof BulkClassSessionDayScheduleSchema
>;
export type BulkCreateClassSessionsDTO = z.infer<
  typeof BulkCreateClassSessionsSchema
>;

/**
 * Update Class Session Schema
 */
export const UpdateClassSessionSchema = z
  .object({
    templateId: z.string().min(1, "ຕ້ອງເລືອກ template").optional(),
    date: z.string().optional(),
    startTime: z.string().optional(),
    endTime: z.string().optional(),
    locationKind: sessionLocationKindSchema.optional(),
    offSiteLabel: z.string().nullable().optional(),
    engagementKind: sessionEngagementKindSchema.optional(),
    roomId: z.union([z.string().min(1, "ຕ້ອງເລືອກຫ້ອງ"), z.null()]).optional(),
    instructors: z.array(SessionInstructorAssignmentSchema).optional(),
    instructorIds: z.array(z.string()).optional(),
    capacity: z
      .number()
      .int("ຄວາມຈຸຕ້ອງເປັນຈຳນວນເຕັມ")
      .positive("ຄວາມຈຸຕ້ອງເປັນຈຳນວນບວກ")
      .optional(),
    notes: z.string().nullable().optional(),
    status: z.enum(["scheduled", "completed", "cancelled"]).optional(),
    changeReason: z.string().min(1, "ຕ້ອງກຳນົດເຫດຜົນການປ່ຽນແປງ").optional(), // Required when there are changes
  })
  .refine((data) => !(data.locationKind === "studio" && data.roomId === null), {
    message: "ຕ້ອງເລືອກຫ້ອງເມື່ອສະຖານທີ່ເປັນສະຕູດິໂອ",
    path: ["roomId"],
  })
  .refine(
    (data) =>
      !(
        data.locationKind === "off_site" &&
        typeof data.roomId === "string" &&
        data.roomId.length > 0
      ),
    {
      message: "ສຳລັບຄລາດສ໌ນອກສະຖານທີ່ບໍ່ຕ້ອງກຳນົດຫ້ອງສະຕູດິໂອ",
      path: ["roomId"],
    },
  )
  .superRefine((data, ctx) => {
    if (data.instructors == null && data.instructorIds == null) return;
    refineSessionInstructors(
      {
        instructors: data.instructors,
        instructorIds: data.instructorIds,
        engagementKind: data.engagementKind ?? "standard",
      },
      ctx,
    );
  });
export type UpdateClassSessionDTO = z.infer<typeof UpdateClassSessionSchema>;

/**
 * Room Conflict Check Schema
 * เมื่อ `sessionLocationKind` เป็น `off_site` จะไม่ตรวจชนห้อง แต่ยังตรวจครูทับเวลาได้
 */
export const RoomConflictCheckSchema = z
  .object({
    roomId: z.string().optional(),
    date: z.string(),
    startTime: z.string(),
    endTime: z.string(),
    /** ເພີ່ມເມື່ອຕ້ອງກວດຄູວ່າວ່າງຫຼືບໍ່ (ຈອງຮຽນສ່ວນຕົວ) */
    instructorIds: z.array(z.string()).optional(),
    excludeSessionId: z.string().optional(),
    excludeBookingId: z.string().optional(),
    excludeRoomBookingId: z.string().optional(),
    /** ประเภทสถานที่ของ session ที่กำลังตรวจ — default studio (เข้ากันกับ client เก่า) */
    sessionLocationKind: sessionLocationKindSchema.optional(),
  })
  .superRefine((data, ctx) => {
    const kind = data.sessionLocationKind ?? "studio";
    const room = data.roomId?.trim() ?? "";
    if (kind === "studio" && room.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ຕ້ອງເລືອກຫ້ອງ",
        path: ["roomId"],
      });
    }
  });
export type RoomConflictCheckDTO = z.infer<typeof RoomConflictCheckSchema>;

/**
 * Room Conflict Result
 */
export const RoomConflictResultSchema = z.object({
  hasConflict: z.boolean(),
  conflictingSessions: z.array(
    z.object({
      id: z.string(),
      date: z.string(),
      startTime: z.string(),
      endTime: z.string(),
      templateName: z.string(),
      instructorNames: z.array(z.string()),
    }),
  ),
  /** ຄູທີ່ເລືອກແລ້ວບໍ່ວ່າງໃນເວລານີ້ */
  busyInstructors: z
    .array(z.object({ id: z.string(), name: z.string() }))
    .optional()
    .default([]),
});
export type RoomConflictResultDTO = z.infer<typeof RoomConflictResultSchema>;

/**
 * Find Alternative Rooms Schema
 */
export const FindAlternativeRoomsSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "ວັນທີບໍ່ຖືກຕ້ອງ"),
  startTime: z.string().regex(/^\d{2}:\d{2}:\d{2}$/, "ເວລາເລີ່ມຕົ້ນບໍ່ຖືກຕ້ອງ"),
  endTime: z.string().regex(/^\d{2}:\d{2}:\d{2}$/, "ເວລາສິ້ນສຸດບໍ່ຖືກຕ້ອງ"),
  excludeRoomId: z.string().optional(),
  minCapacity: z.number().int().positive().optional(),
});
export type FindAlternativeRoomsDTO = z.infer<
  typeof FindAlternativeRoomsSchema
>;

/**
 * Alternative Room DTO
 */
export const AlternativeRoomSchema = z.object({
  id: z.string(),
  name: z.string(),
  capacity: z.number().nullable(),
  description: z.string().nullable(),
});
export type AlternativeRoomDTO = z.infer<typeof AlternativeRoomSchema>;

/**
 * Schedule Change Log Schema
 */
export const ScheduleChangeLogSchema = z.object({
  id: z.string(),
  sessionType: z.enum(["class_session"]),
  classSessionId: z.string(),
  changeType: z.enum([
    "time",
    "room",
    "instructor",
    "capacity",
    "location",
    "engagement",
    "other",
  ]),
  oldValue: z.string().nullable(),
  newValue: z.string().nullable(),
  reason: z.string(),
  changedBy: z.string(),
  changedByUserName: z.string().optional(), // Join from users table
  changedAt: z.string(),
  createdAt: z.string(),
});

export type ScheduleChangeLogDTO = z.infer<typeof ScheduleChangeLogSchema>;

/**
 * Bulk update template for class sessions matching list filters.
 * Only updates sessions with status = scheduled.
 */
export const BulkUpdateClassSessionTemplateSchema = z.object({
  filters: z.array(FilterConditionSchema).default([]),
  templateId: z.string().min(1, "ຕ້ອງເລືອກແມ່ແບບຄລາດສ໌"),
  changeReason: z.string().min(1, "ຕ້ອງກຳນົດເຫດຜົນການປ່ຽນແປງ"),
});

export type BulkUpdateClassSessionTemplateDTO = z.infer<
  typeof BulkUpdateClassSessionTemplateSchema
>;

export const BulkUpdateClassSessionTemplatePreviewSchema =
  BulkUpdateClassSessionTemplateSchema.pick({
    filters: true,
    templateId: true,
  });

export type BulkUpdateClassSessionTemplatePreviewDTO = z.infer<
  typeof BulkUpdateClassSessionTemplatePreviewSchema
>;

export const BulkUpdateClassSessionPreviewItemSchema = z.object({
  id: z.string(),
  date: z.string(),
  startTime: z.string(),
  endTime: z.string(),
  templateId: z.string(),
  templateName: z.string(),
  roomName: z.string().nullable(),
});

export type BulkUpdateClassSessionPreviewItemDTO = z.infer<
  typeof BulkUpdateClassSessionPreviewItemSchema
>;

export const BulkUpdateClassSessionTemplatePreviewResultSchema = z.object({
  totalMatching: z.number(),
  scheduledMatching: z.number(),
  updatableCount: z.number(),
  sessions: z.array(BulkUpdateClassSessionPreviewItemSchema),
  sessionsTruncated: z.boolean(),
});

export type BulkUpdateClassSessionTemplatePreviewResultDTO = z.infer<
  typeof BulkUpdateClassSessionTemplatePreviewResultSchema
>;

/**
 * Bulk update instructors for class sessions matching list filters.
 * Only updates sessions with status = scheduled.
 */
const BulkUpdateClassSessionInstructorsBodySchema = z.object({
  filters: z.array(FilterConditionSchema).default([]),
  instructors: z.array(SessionInstructorAssignmentSchema).min(1),
  changeReason: z.string().min(1, "ຕ້ອງກຳນົດເຫດຜົນການປ່ຽນແປງ"),
});

export const BulkUpdateClassSessionInstructorsSchema =
  BulkUpdateClassSessionInstructorsBodySchema.superRefine((data, ctx) => {
    refineSessionInstructors(
      {
        instructors: data.instructors,
        engagementKind: "standard",
      },
      ctx,
    );
  });

export type BulkUpdateClassSessionInstructorsDTO = z.infer<
  typeof BulkUpdateClassSessionInstructorsSchema
>;

export const BulkUpdateClassSessionInstructorsPreviewSchema =
  BulkUpdateClassSessionInstructorsBodySchema.pick({
    filters: true,
    instructors: true,
  }).superRefine((data, ctx) => {
    refineSessionInstructors(
      {
        instructors: data.instructors,
        engagementKind: "standard",
      },
      ctx,
    );
  });

export type BulkUpdateClassSessionInstructorsPreviewDTO = z.infer<
  typeof BulkUpdateClassSessionInstructorsPreviewSchema
>;

export const BulkUpdateClassSessionInstructorPreviewItemSchema =
  BulkUpdateClassSessionPreviewItemSchema.extend({
    instructorNames: z.string(),
  });

export type BulkUpdateClassSessionInstructorPreviewItemDTO = z.infer<
  typeof BulkUpdateClassSessionInstructorPreviewItemSchema
>;

export const BulkUpdateClassSessionInstructorsPreviewResultSchema = z.object({
  totalMatching: z.number(),
  scheduledMatching: z.number(),
  updatableCount: z.number(),
  sessions: z.array(BulkUpdateClassSessionInstructorPreviewItemSchema),
  sessionsTruncated: z.boolean(),
});

export type BulkUpdateClassSessionInstructorsPreviewResultDTO = z.infer<
  typeof BulkUpdateClassSessionInstructorsPreviewResultSchema
>;
